// Coach "Daily Lesson Plan": the child-wise lesson plan the admin created for a Balgruha visit
// (see pages/AdminLessonPlan.jsx), plus the Balgruha's previous two visits for reference.
// The plan comes from the admin, so coaches only read it here (filters, share and PDF still work).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle,
    BookOpen,
    CalendarDays,
    ChevronDown,
    Clock,
    Copy,
    Download,
    ExternalLink,
    Eye,
    File as FileIcon,
    FileImage,
    FileText,
    Heart,
    House,
    Mail,
    MessageCircle,
    Monitor,
    MoreHorizontal,
    Paperclip,
    Share2,
    X,
} from 'lucide-react';
import { getScheduleAttachmentBlob, getScheduleBalagruhas, getScheduleShareKey, getSchedulesCoach } from '../../api';
import showToast from '../../utils/toast';
import { NativePickerField, escapeHtml } from '../../pages/dailyScheduleShared';
import { isLocalLink, visitLinkText, visitShareUrl } from '../../pages/DailyScheduleVisitModals';
import { LEARNING_COLUMNS } from '../../pages/lessonPlanColumns';
import {
    FixedLearning,
    FixedValue,
    PlanTableHead,
    fixedAppMeta,
    rowLearning,
} from '../../pages/lessonPlanCells';
import {
    COMPUTER_APPS,
    appMeta,
    rowCategory,
    sortCategories,
} from '../../constants/lessonPlanCatalog';
import { downloadLessonPlanPdf } from './dailyLessonPlanPdf';
import './DailyLessonPlan.css';
import SearchableSelect from '../common/SearchableSelect';

// Local date parts, not toISOString (which is UTC and can roll the date back in IST).
const toKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fromKey = (key) => new Date(`${key}T00:00:00`);
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const displayDate = (key) => {
    const d = fromKey(key);
    return `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getFullYear()}`;
};
const displayTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
const idOf = (v) => (v && typeof v === 'object' ? v._id || v.id : v) || '';
const centreOf = (s) => idOf(s.balagruhaId) || idOf(s.balagruha);
const coachOf = (s) => idOf(s.assignedTo) || idOf(s.assignedToUser);
const flatten = (result) => (result?.data?.schedules || []).flatMap((day) => day.schedules || []);
const unique = (values) => [...new Set(values.filter(Boolean))];
const planRows = (schedules) => schedules.find((s) => s.lessonPlan?.children?.length)?.lessonPlan.children || [];

// Highlights "@Child Name" tags written in the admin's Health Check notes.
const TaggedText = ({ text, names }) => {
    if (!text) return null;
    const escaped = names.filter(Boolean).sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!escaped.length) return text;
    // "@all" (the admin's tag for every child, see MentionTextarea) is highlighted like a name.
    escaped.push('all(?![\\w])');
    return text.split(new RegExp(`(@(?:${escaped.join('|')}))`, 'g')).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
};

const fileSize = (bytes) => (!bytes ? '' : bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// Each coach on a visit has their own copy of the admin's attachments (same S3 file), so de-duplicate
// by storage key and remember which schedule to fetch each one through.
const attachmentsOf = (schedules) => {
    const seen = new Set();
    return schedules.flatMap((s) => (s.attachments || []).map((a) => ({ ...a, scheduleId: s._id })))
        .filter((a) => { const key = a.s3Key || a._id; if (seen.has(key)) return false; seen.add(key); return true; });
};

function Attachments({ items }) {
    if (!items.length) return null;
    // The file route needs the auth header, so fetch the file and hand the browser a blob URL.
    const open = async (item, download) => {
        const tab = download ? null : window.open('', '_blank'); // opened synchronously so it isn't popup-blocked
        try {
            const blob = await getScheduleAttachmentBlob(item.scheduleId, item._id);
            const url = URL.createObjectURL(new Blob([blob], { type: item.mimeType || blob.type }));
            if (download) {
                const a = document.createElement('a');
                a.href = url;
                a.download = item.filename || 'attachment';
                a.click();
            } else if (tab) {
                tab.location.href = url;
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            if (tab) tab.close();
            showToast('Unable to open the attachment', 'error');
        }
    };
    return (
        <div className="dlp-note dlp-attachments">
            <h4><Paperclip size={16} />Attachments ({items.length})</h4>
            <ul>
                {items.map((item) => {
                    const Icon = (item.mimeType || '').startsWith('image/') ? FileImage : item.mimeType === 'application/pdf' ? FileText : FileIcon;
                    return (
                        <li key={item.s3Key || item._id}>
                            <Icon size={18} />
                            <span className="dlp-attachments__name" title={item.filename}>{item.filename}</span>
                            {item.size ? <small>{fileSize(item.size)}</small> : null}
                            <button type="button" className="dlp-view" onClick={() => open(item, false)}><Eye size={15} />View</button>
                            <button type="button" className="dlp-view" onClick={() => open(item, true)}><Download size={15} />Download</button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

// The admin's Create Schedule table (Computer Apps, one column per One-on-One subject, Special
// Instructions), read-only: the One-on-One columns are read from the row's saved `oneOnOne` text.
function PlanTable({ rows }) {
    return (
        <div className="lp-table-wrap dlp-plan-wrap">
            <table className="lp-table lp-table--plan dlp-plan-table">
                <PlanTableHead />
                <tbody>
                    {rows.map(({ row, index }) => {
                        const learning = rowLearning(row);
                        const computer = row.computer || COMPUTER_APPS[0].name;
                        return (
                            <tr key={row.childId || index}>
                                <td>{index + 1}</td>
                                <td><b title={row.childName}>{row.childName}</b></td>
                                <td><FixedValue value={computer} meta={fixedAppMeta} /></td>
                                {LEARNING_COLUMNS.map(({ key }) => <td key={key}><FixedLearning column={key} value={learning[key]} /></td>)}
                                <td>
                                    {/* Special instructions come from the admin's plan; coaches only read them. */}
                                    <input className="lp-input dlp-input" value={row.note || ''} readOnly title={row.note || undefined} aria-label={`Special instructions for ${row.childName}`} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// Health check, instructions, learning focus and files of one visit, shown under its plan.
function VisitDetails({ first, childNames, attachments }) {
    return (
        <>
            {(first?.healthCheck || first?.specialInstructions || first?.learningsFocus) && (
                <div className="dlp-notes">
                    {first.healthCheck && (
                        <div className="dlp-note dlp-note--health">
                            <h4><Heart size={16} fill="currentColor" />Health Check</h4>
                            <p><TaggedText text={first.healthCheck} names={childNames} /></p>
                        </div>
                    )}
                    {first.specialInstructions && (
                        <div className="dlp-note">
                            <h4><FileText size={16} />General Instructions</h4>
                            <p>{first.specialInstructions}</p>
                        </div>
                    )}
                    {first.learningsFocus && (
                        <div className="dlp-note">
                            <h4><BookOpen size={16} />Learning Focus</h4>
                            <p>{first.learningsFocus}</p>
                        </div>
                    )}
                </div>
            )}

            <Attachments items={attachments} />
        </>
    );
}

// Balgruha filter value for "All Balgruhas" (the default): one block per Balgruha the coach has a planned visit at that day.
const ALL_CENTRES = 'all';
const timeRange = (s) => `${displayTime(s.startTime)} – ${displayTime(s.endTime)}`;
const coachNamesOf = (list) => unique(list.map(coachOf)).map((id) => list.find((s) => coachOf(s) === id)?.assignedToUser?.name || 'Coach').join(', ');
const planLines = (rows) => rows.map((r, i) => `${i + 1}. ${r.childName} — Computer: ${r.computer || '-'}; One-on-One: ${r.oneOnOne || '-'}${r.note ? `; ${r.note}` : ''}`);
const noteLines = (first, schedules) => {
    const lines = [];
    if (first?.healthCheck) lines.push('', `Health check: ${first.healthCheck}`);
    if (first?.specialInstructions) lines.push('', `Instructions: ${first.specialInstructions}`);
    const files = attachmentsOf(schedules);
    if (files.length) lines.push('', `Attachments: ${files.map((a) => a.filename).join(', ')}`);
    return lines;
};
// One visit's part of the PDF (dailyLessonPlanPdf.js): plan rows, time, coaches and notes.
const pdfVisit = (name, list, plan, files) => ({
    name,
    start: list[0] ? displayTime(list[0].startTime) : '',
    end: list[0] ? displayTime(list[0].endTime) : '',
    coaches: coachNamesOf(list),
    rows: plan,
    healthCheck: list[0]?.healthCheck,
    instructions: list[0]?.specialInstructions,
    attachments: attachmentsOf(files).map((a) => a.filename || 'Attachment'),
});

function DailySchedule({ selectedDate, setSelectedDate, balagruhaOptions, selectedBalagruhaOfCoach }) {
    const myId = localStorage.getItem('userId') || '';
    const dateKey = toKey(selectedDate);
    // Every Balgruha is listed. In the coach's own (balagruhaOptions) they see all visits, in the
    // others only the visits assigned to them. Falls back to their own if the list can't be loaded.
    const [allCentres, setAllCentres] = useState(); // undefined while loading
    useEffect(() => {
        let cancelled = false;
        getScheduleBalagruhas()
            .then((list) => { if (!cancelled) setAllCentres(list); })
            .catch(() => { if (!cancelled) setAllCentres([]); });
        return () => { cancelled = true; };
    }, []);
    const centres = useMemo(() => (allCentres?.length ? allCentres : balagruhaOptions || []), [allCentres, balagruhaOptions]);
    const assigned = useMemo(() => new Set((balagruhaOptions || []).map((b) => b._id)), [balagruhaOptions]);
    const centresReady = allCentres !== undefined;
    const [daySchedules, setDaySchedules] = useState([]);
    const [history, setHistory] = useState([]);
    const [centreId, setCentreId] = useState(ALL_CENTRES);
    const [coachId, setCoachId] = useState('');
    const [category, setCategory] = useState('All');
    const [app, setApp] = useState('All');
    const [loading, setLoading] = useState(true);
    const [shareOpen, setShareOpen] = useState(false);
    // Links to the read-only shared page (as the admin's Share visit), one per Balgruha being shared.
    const [shareLinks, setShareLinks] = useState({ sig: '', status: 'idle', urls: {} });
    const [viewing, setViewing] = useState(null);
    const previousRef = useRef(null);
    const isAll = centreId === ALL_CENTRES;

    // Every visit on the selected day across the coach's Balgruhas, so we can pick a sensible default.
    useEffect(() => {
        if (!centresReady) return undefined;
        const ids = centres.map((b) => b._id);
        if (!ids.length) { setLoading(false); return undefined; }
        let cancelled = false;
        setLoading(true);
        getSchedulesCoach({ balagruhaIds: ids, startDate: dateKey, endDate: dateKey })
            .then((result) => {
                if (cancelled) return;
                const list = flatten(result).filter((s) => s.status !== 'cancelled');
                setDaySchedules(list);
                setCentreId((current) => {
                    if (current === ALL_CENTRES) return current;
                    if (current && list.some((s) => centreOf(s) === current)) return current;
                    const mine = list.find((s) => coachOf(s) === myId);
                    const fallback = (Array.isArray(selectedBalagruhaOfCoach) ? selectedBalagruhaOfCoach[0] : selectedBalagruhaOfCoach) || ids[0];
                    return centreOf(mine || list[0] || {}) || current || fallback;
                });
            })
            .catch(() => { if (!cancelled) { setDaySchedules([]); showToast('Unable to load the lesson plan', 'error'); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [centres, centresReady, dateKey, myId, selectedBalagruhaOfCoach]);

    // Earlier visits to the selected Balgruha (or to all of them), for "Previous Plans".
    const historyKey = !centresReady ? '' : isAll ? centres.map((b) => b._id).join(',') : centreId;
    useEffect(() => {
        const ids = historyKey.split(',').filter(Boolean);
        if (!ids.length) { setHistory([]); return undefined; }
        let cancelled = false;
        const end = addDays(fromKey(dateKey), -1);
        getSchedulesCoach({ balagruhaIds: ids, startDate: toKey(addDays(end, -365)), endDate: toKey(end) })
            .then((result) => { if (!cancelled) setHistory(flatten(result).filter((s) => s.status !== 'cancelled' && ids.includes(centreOf(s)))); })
            .catch(() => { if (!cancelled) setHistory([]); });
        return () => { cancelled = true; };
    }, [historyKey, dateKey]);

    const visit = useMemo(() => (isAll ? daySchedules : daySchedules.filter((s) => centreOf(s) === centreId)), [daySchedules, centreId, isAll]);
    const visitCoaches = useMemo(() => unique(visit.map(coachOf)).map((id) => ({ id, name: visit.find((s) => coachOf(s) === id)?.assignedToUser?.name || 'Coach' })), [visit]);

    useEffect(() => {
        setCoachId((current) => (visitCoaches.some((c) => c.id === current) ? current : visitCoaches.some((c) => c.id === myId) ? myId : ''));
    }, [visitCoaches, myId]);

    const shown = useMemo(() => (coachId ? visit.filter((s) => coachOf(s) === coachId) : visit), [visit, coachId]);
    // One block per Balgruha: the selected one, or (All Balgruhas) only those where the selected coach has
    // a visit that day with a child-wise plan from the admin.
    const groups = useMemo(() => {
        const list = (isAll ? centres.map((b) => b._id) : [centreId]).map((id) => ({
            centreId: id,
            name: centres.find((b) => b._id === id)?.name || visit.find((s) => centreOf(s) === id)?.balagruha?.name || '',
            assigned: assigned.has(id),
            visit: visit.filter((s) => centreOf(s) === id),
            shown: shown.filter((s) => centreOf(s) === id),
        }));
        return isAll ? list.filter((g) => planRows(g.shown).length) : list;
    }, [isAll, centres, assigned, centreId, visit, shown]);
    // Each shown Balgruha's plan rows, as the admin saved them.
    const plans = useMemo(() => Object.fromEntries(groups.map((g) => [g.centreId, planRows(g.shown)])), [groups]);
    const rows = useMemo(() => groups.flatMap((g) => plans[g.centreId] || []), [groups, plans]);

    const centreName = isAll ? 'All Balgruhas' : groups[0]?.name || '';
    const coachNames = (coachId ? visitCoaches.filter((c) => c.id === coachId) : visitCoaches).map((c) => c.name).join(', ');
    const first = shown[0];
    const childNames = rows.map((r) => r.childName);
    // Category (One-on-One) and Computer App filters combine; each one's counts respect the other.
    const appOf = (r) => r.computer || COMPUTER_APPS[0].name;
    const matchesApp = (r) => app === 'All' || appOf(r) === app;
    const matchesCategory = (r) => category === 'All' || rowCategory(r) === category;
    const tally = (list, keyOf) => list.reduce((map, r) => { const k = keyOf(r); map[k] = (map[k] || 0) + 1; return map; }, {});
    const counts = useMemo(() => tally(rows.filter(matchesApp), rowCategory), [rows, app]); // eslint-disable-line react-hooks/exhaustive-deps
    const appCounts = useMemo(() => tally(rows.filter(matchesCategory), appOf), [rows, category]); // eslint-disable-line react-hooks/exhaustive-deps
    const allCategories = useMemo(() => sortCategories(unique(rows.map(rowCategory))), [rows]);
    const allApps = useMemo(() => {
        const used = unique(rows.map(appOf));
        // Catalogue order first, then any app that is no longer in the catalogue.
        return [...COMPUTER_APPS.map((a) => a.name).filter((n) => used.includes(n)), ...used.filter((n) => !COMPUTER_APPS.some((a) => a.name === n))];
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
    const tabs = allCategories;
    const { Icon: AppIcon, color: appIconColor } = app === 'All' ? { Icon: Monitor, color: '#0b2fb0' } : appMeta(app);
    const visibleOf = (list) => list.map((row, index) => ({ row, index })).filter(({ row }) => matchesCategory(row) && matchesApp(row));
    const visibleRows = visibleOf(rows);
    // "All Balgruhas" shows only the Balgruhas with something to show: no empty block for a Balgruha
    // whose children are all filtered out by Category / Computer App.
    const visibleGroups = groups.filter((g) => visibleOf(plans[g.centreId] || []).length);
    useEffect(() => { if (category !== 'All' && !allCategories.includes(category)) setCategory('All'); }, [allCategories, category]);
    useEffect(() => { if (app !== 'All' && !allApps.includes(app)) setApp('All'); }, [allApps, app]);

    // "All Balgruhas": Previous Plans lists only the Balgruhas whose plan is shown above (all of them when none is).
    const previousCentres = useMemo(() => (isAll && groups.length ? groups.map((g) => g.centreId) : null), [isAll, groups]);
    const previousName = isAll ? groups.map((g) => g.name).filter(Boolean).join(', ') : centreName;
    const previous = useMemo(() => {
        const byVisit = {};
        history.filter((s) => !previousCentres || previousCentres.includes(centreOf(s))).forEach((s) => {
            const date = String(s.date).slice(0, 10);
            (byVisit[`${date}|${centreOf(s)}`] ||= { date, centreId: centreOf(s), list: [] }).list.push(s);
        });
        // The two latest visits of each Balgruha.
        const perCentre = {};
        return Object.values(byVisit)
            .sort((a, b) => (a.date < b.date ? 1 : -1))
            .filter((v) => { perCentre[v.centreId] = (perCentre[v.centreId] || 0) + 1; return perCentre[v.centreId] <= 2; })
            .map(({ date, centreId: id, list }) => {
                const plan = planRows(list);
                return {
                    key: `${date}|${id}`,
                    date,
                    centreName: centres.find((b) => b._id === id)?.name || list[0]?.balagruha?.name || '',
                    list,
                    plan,
                    coaches: unique(list.map((s) => s.assignedToUser?.name)).join(', '),
                    createdBy: unique(list.map((s) => s.createdByUser?.name)).join(', '),
                    children: plan.length,
                    attachments: attachmentsOf(list).length,
                    course: sortCategories(unique(plan.map(rowCategory))).join(', ') || unique(list.map((s) => s.lessonPlan?.course || s.learningsFocus)).join(', ') || '—',
                };
            });
    }, [history, centres, previousCentres]);

    const shareText = () => {
        if (isAll) {
            const lines = [`Daily Lesson Plan — ${centreName}`, displayDate(dateKey)];
            groups.filter((g) => g.shown.length).forEach((g) => {
                lines.push('', `${g.name}, ${timeRange(g.shown[0])}`, `Coach: ${coachNamesOf(g.shown)}`, ...planLines(plans[g.centreId] || []), ...noteLines(g.shown[0], g.visit));
            });
            return lines.join('\n');
        }
        return [
            `Daily Lesson Plan — ${centreName}`,
            `${displayDate(dateKey)}${first ? `, ${timeRange(first)}` : ''}`,
            `Coach: ${coachNames}`,
            '',
            ...planLines(rows),
            ...noteLines(first, visit),
        ].join('\n');
    };
    // The visits Share Plan sends: every shown Balgruha (All Balgruhas) or the selected one. `day` is the
    // UTC day of the visit's date, which is how the shared page (and the admin's Daily Schedule) reads it.
    const withDay = (t) => ({ ...t, day: String(t.first?.date || t.first?.startTime || '').slice(0, 10) || dateKey });
    const shareTargets = () => (isAll
        ? groups.filter((g) => g.shown.length).map((g) => ({ centreId: g.centreId, name: g.name, first: g.shown[0], coaches: coachNamesOf(g.shown) }))
        : visit.length ? [{ centreId, name: centreName, first: first || visit[0], coaches: coachNames }] : []).map(withDay);
    // While the menu is open, the links for what it would share: [Balgruha id, visit day, name] each.
    const shareSig = shareOpen
        ? JSON.stringify(shareTargets().map((t) => [t.centreId, t.day, t.name]))
        : '';
    useEffect(() => {
        if (!shareSig) return undefined;
        let cancelled = false;
        setShareLinks({ sig: shareSig, status: 'loading', urls: {} });
        Promise.all(JSON.parse(shareSig).map(async ([id, date, name]) => {
            const key = await getScheduleShareKey(id, date);
            if (!key) throw new Error('No share key');
            return [id, visitShareUrl({ date, centreName: name, key })];
        }))
            .then((entries) => { if (!cancelled) setShareLinks({ sig: shareSig, status: 'ready', urls: Object.fromEntries(entries) }); })
            .catch(() => { if (!cancelled) setShareLinks({ sig: shareSig, status: 'error', urls: {} }); });
        return () => { cancelled = true; };
    }, [shareSig]);
    // Until the links are ready the options wait, so WhatsApp still opens from the click.
    const linksStatus = shareSig && shareLinks.sig === shareSig ? shareLinks.status : 'loading';
    const linksLoading = linksStatus === 'loading';
    const readyLinks = shareSig && linksStatus === 'ready' ? shareTargets().map((t) => ({ ...t, url: shareLinks.urls[t.centreId] })) : [];
    // In the admin's Share visit format ("Sanstha : X", "Time : …", "ISF Faculty : …", "Date : …", "Day : …",
    // link), one block per Balgruha. The link sits on a line of its own, so apps turn it into a link.
    const linkText = (links) => links.map((l) => visitLinkText({
        visit: { date: l.day, startTime: l.first.startTime, endTime: l.first.endTime },
        centreName: l.name,
        coachNames: l.coaches,
        url: l.url,
    })).join('\n\n');
    // Copied as plain text and as HTML, so pasting into mail, Docs etc. gives clickable links.
    const copyWithLinks = async (text) => {
        if (typeof ClipboardItem !== 'function' || typeof navigator.clipboard?.write !== 'function') return false;
        const html = text.split('\n').map((line) => (/^https?:\/\//.test(line) ? `<a href="${escapeHtml(line)}">${escapeHtml(line)}</a>` : escapeHtml(line))).join('<br>');
        try {
            await navigator.clipboard.write([new ClipboardItem({
                'text/plain': new Blob([text], { type: 'text/plain' }),
                'text/html': new Blob([html], { type: 'text/html' }),
            })]);
            return true;
        } catch {
            return false; // e.g. rich clipboard blocked; the plain text below still has the links
        }
    };
    // Sends the link(s); if they couldn't be made, the plan goes out as text, as before.
    const share = async (how) => {
        setShareOpen(false);
        const links = readyLinks;
        const text = links.length ? linkText(links) : shareText();
        if (how === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
        else if (how === 'email') window.location.href = `mailto:?subject=${encodeURIComponent(`Lesson Plan — ${centreName}, ${displayDate(dateKey)}`)}&body=${encodeURIComponent(text)}`;
        else if (how === 'more' && navigator.share) {
            // `url` makes the share sheet hand the app a link, not just a message.
            const data = links.length === 1 && !isAll
                ? { title: 'Lesson Plan', text: text.slice(0, text.lastIndexOf('\n')), url: links[0].url }
                : { title: 'Lesson Plan', text };
            try { await navigator.share(data); } catch { /* dismissed */ }
        } else {
            try {
                if (!(links.length && await copyWithLinks(text))) await navigator.clipboard.writeText(text);
                showToast(links.length ? 'Lesson plan link copied to clipboard' : 'Lesson plan copied to clipboard', 'success');
            } catch { showToast('Unable to copy the lesson plan', 'error'); }
        }
    };

    // Laid out like the admin's Create Schedule page (see dailyLessonPlanPdf.js).
    const downloadPdf = async () => {
        try {
            await downloadLessonPlanPdf({
                fileName: `lesson-plan-${centreName.replace(/\s+/g, '-').toLowerCase() || 'centre'}-${dateKey}.pdf`,
                date: displayDate(dateKey),
                centreName,
                coachNames,
                allCentres: isAll,
                visits: isAll
                    ? groups.filter((g) => (plans[g.centreId] || []).length).map((g) => pdfVisit(g.name, g.shown, plans[g.centreId], g.visit))
                    : [pdfVisit(centreName, shown, rows, visit)],
            });
        } catch {
            showToast('Unable to create the PDF', 'error');
        }
    };

    const previousButton = (
        <div className="dlp-info__actions">
            <button type="button" className="dlp-btn dlp-btn--outline" onClick={() => previousRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <Clock size={16} />Previous Plans
            </button>
        </div>
    );

    return (
        <div className="dlp">
            <header className="dlp-header">
                <div className="dlp-title">
                    <span className="dlp-title__icon"><BookOpen size={30} strokeWidth={1.8} /></span>
                    <div>
                        <h3>Daily Lesson Plan</h3>
                        <p>View your lesson plan for {dateKey === toKey(new Date()) ? 'today' : displayDate(dateKey)} (created by Admin)</p>
                    </div>
                </div>
                <div className="dlp-filters">
                    <label>
                        Balgruha
                        <span className="dlp-filter">
                            <SearchableSelect searchPlaceholder="Search balagruha..." value={centreId} onChange={(e) => setCentreId(e.target.value)}>
                                <option value={ALL_CENTRES}>All Balgruhas</option>
                                {centres.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                            </SearchableSelect>
                            <ChevronDown size={15} />
                        </span>
                    </label>
                    <label>
                        Date
                        <NativePickerField
                            type="date"
                            chevron
                            icon={<CalendarDays size={15} />}
                            value={dateKey}
                            display={displayDate(dateKey)}
                            onChange={(value) => { if (value) setSelectedDate(fromKey(value)); }}
                            className="dlp-date"
                        />
                    </label>
                    <label>
                        Coach
                        <span className="dlp-filter">
                            <SearchableSelect searchPlaceholder="Search coach..." value={coachId} onChange={(e) => setCoachId(e.target.value)}>
                                <option value="">All Coaches</option>
                                {visitCoaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </SearchableSelect>
                            <ChevronDown size={15} />
                        </span>
                    </label>
                    <label>
                        Category
                        <span className="dlp-filter">
                            <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                <option value="All">All Categories</option>
                                {tabs.map((c) => <option key={c} value={c}>{c} ({counts[c] || 0})</option>)}
                            </select>
                            <ChevronDown size={15} />
                        </span>
                    </label>
                    <label>
                        Computer App
                        <span className="dlp-filter dlp-filter--icon">
                            <AppIcon size={16} color={appIconColor} className="dlp-filter__icon" />
                            <SearchableSelect searchPlaceholder="Search app..." value={app} onChange={(e) => setApp(e.target.value)}>
                                <option value="All">All Apps</option>
                                {allApps.map((a) => <option key={a} value={a}>{a} ({appCounts[a] || 0})</option>)}
                            </SearchableSelect>
                            <ChevronDown size={15} />
                        </span>
                    </label>
                </div>
            </header>

            {/* The category chips (All / Action Songs …) are gone; the Category filter above does the same. */}
            <div className="dlp-tabs">
                <div className="dlp-tabs__actions">
                    <div className="dlp-share">
                        <button type="button" className="dlp-btn dlp-btn--outline" disabled={isAll ? !groups.length : !visit.length} onClick={() => setShareOpen((o) => !o)} aria-expanded={shareOpen}>
                            <Share2 size={17} />Share Plan
                        </button>
                        {shareOpen && (
                            <div className="dlp-share__menu" role="menu">
                                <button type="button" role="menuitem" disabled={linksLoading} onClick={() => share('whatsapp')}><MessageCircle size={16} />WhatsApp</button>
                                <button type="button" role="menuitem" disabled={linksLoading} onClick={() => share('email')}><Mail size={16} />Email</button>
                                <button type="button" role="menuitem" disabled={linksLoading} onClick={() => share('copy')}><Copy size={16} />Copy plan</button>
                                {navigator.share && <button type="button" role="menuitem" disabled={linksLoading} onClick={() => share('more')}><MoreHorizontal size={16} />More options</button>}
                                {linksLoading && <p className="dlp-share__note" role="status">Creating link…</p>}
                                {readyLinks.length > 0 && (
                                    <div className="dlp-share__links">
                                        {readyLinks.map((l) => (
                                            <a key={l.centreId} role="menuitem" href={l.url} target="_blank" rel="noopener noreferrer" title={l.url} onClick={() => setShareOpen(false)}>
                                                <ExternalLink size={16} />Open link{isAll && <small>{l.name}</small>}
                                            </a>
                                        ))}
                                    </div>
                                )}
                                {readyLinks.some((l) => isLocalLink(l.url)) && (
                                    <p className="dlp-share__note" role="note">
                                        <AlertTriangle size={14} />This link starts with localhost, so it only opens on this computer and WhatsApp shows it as plain text. Links shared from the live site are clickable.
                                    </p>
                                )}
                                {linksStatus === 'error' && (
                                    <p className="dlp-share__note" role="status"><AlertTriangle size={14} />Could not create a link, so the plan will be shared as text.</p>
                                )}
                            </div>
                        )}
                    </div>
                    <button type="button" className="dlp-btn dlp-btn--primary" disabled={!rows.length} onClick={downloadPdf}><Download size={17} />Download (PDF)</button>
                </div>
            </div>

            {/* "All Balgruhas" has no summary bar of its own: each Balgruha's block has its bar, and
                the first one carries Previous Plans. */}
            {!isAll && (
                <div className="dlp-info">
                    <span><House size={16} fill="currentColor" />Centre : <b>{centreName || '—'}</b></span>
                    <span>Date : <b>{displayDate(dateKey)}</b>{first && <small> ({timeRange(first)})</small>}</span>
                    <span>Coach : <b>{coachNames || '—'}</b></span>
                    {previousButton}
                </div>
            )}

            {loading ? (
                <div className="dlp-empty">Loading lesson plan…</div>
            ) : isAll ? (
                visibleGroups.length ? (
                    visibleGroups.map((g, i) => {
                        const groupRows = plans[g.centreId] || [];
                        const groupFirst = g.shown[0];
                        return (
                            <div className="dlp-group" key={g.centreId} role="region" aria-label={`${g.name} lesson plan`}>
                                <div className="dlp-info dlp-info--group">
                                    <span><House size={16} fill="currentColor" />Centre : <b>{g.name || '—'}</b></span>
                                    <span>Time : <b>{timeRange(groupFirst)}</b></span>
                                    <span>Coach : <b>{coachNamesOf(g.shown)}</b></span>
                                    {i === 0 && previousButton}
                                </div>
                                <PlanTable rows={visibleOf(groupRows)} />
                                <VisitDetails first={groupFirst} childNames={groupRows.map((r) => r.childName)} attachments={attachmentsOf(g.visit)} />
                            </div>
                        );
                    })
                ) : groups.length ? (
                    <div className="dlp-empty">No children match the selected course and computer app.</div>
                ) : shown.length ? (
                    <div className="dlp-empty">The admin hasn't added a child-wise lesson plan for this visit yet.</div>
                ) : <div className="dlp-empty">No visit is scheduled on {displayDate(dateKey)}.</div>
            ) : !visit.length ? (
                <div className="dlp-empty">{groups[0]?.assigned === false ? 'You have no visit' : 'No visit is scheduled'} at {centreName || 'this Balgruha'} on {displayDate(dateKey)}.</div>
            ) : !rows.length ? (
                <div className="dlp-empty">The admin hasn't added a child-wise lesson plan for this visit yet.</div>
            ) : !visibleRows.length ? (
                <div className="dlp-empty">No children match the selected course and computer app.</div>
            ) : (
                <PlanTable rows={visibleRows} />
            )}

            {!isAll && <VisitDetails first={first} childNames={childNames} attachments={attachmentsOf(visit)} />}

            <section className="dlp-previous" ref={previousRef}>
                <h4><Clock size={18} />Previous Plans <small>(For Reference)</small></h4>
                <div className="dlp-table-wrap">
                    <table className="dlp-table dlp-table--compact">
                        <thead><tr><th>Date</th>{isAll && <th>Balgruha</th>}<th>Coach (Created by)</th><th>Total Children</th><th>Category</th><th>Action</th></tr></thead>
                        <tbody>
                            {previous.map((p) => (
                                <tr key={p.key}>
                                    <td>{displayDate(p.date)}</td>
                                    {isAll && <td>{p.centreName || '—'}</td>}
                                    <td>{p.coaches || '—'}{p.createdBy && <small> (by {p.createdBy})</small>}</td>
                                    <td>{p.children || '—'}</td>
                                    <td>{p.course}{p.attachments ? <small className="dlp-clip"><Paperclip size={13} />{p.attachments}</small> : null}</td>
                                    <td><button type="button" className="dlp-view" onClick={() => setViewing(p)}><Eye size={15} />View</button></td>
                                </tr>
                            ))}
                            {!previous.length && <tr><td colSpan={isAll ? 6 : 5} className="dlp-empty-cell">{isAll && !previousCentres ? 'No earlier visits to your Balgruhas.' : `No earlier visits to ${previousName || 'this Balgruha'}.`}</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>

            {viewing && (
                <div className="dlp-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setViewing(null); }}>
                    <div className="dlp-modal__card" role="dialog" aria-modal="true" aria-labelledby="dlp-modal-title">
                        <div className="dlp-modal__head">
                            <div>
                                <h3 id="dlp-modal-title">Visit on {displayDate(viewing.date)}</h3>
                                <p>{viewing.centreName} · {viewing.coaches} · {displayTime(viewing.list[0].startTime)} – {displayTime(viewing.list[0].endTime)}</p>
                            </div>
                            <button type="button" aria-label="Close" onClick={() => setViewing(null)}><X size={20} /></button>
                        </div>
                        {viewing.plan.length
                            ? <PlanTable rows={viewing.plan.map((row, index) => ({ row, index }))} />
                            : <div className="dlp-empty">No child-wise plan was recorded for this visit.</div>}
                        <div className="dlp-notes">
                            {unique(viewing.list.map((s) => s.healthCheck)).map((t) => (
                                <div key={t} className="dlp-note dlp-note--health"><h4><Heart size={16} fill="currentColor" />Health Check</h4><p><TaggedText text={t} names={viewing.plan.map((r) => r.childName)} /></p></div>
                            ))}
                            {unique(viewing.list.map((s) => s.specialInstructions)).map((t) => (
                                <div key={t} className="dlp-note"><h4><FileText size={16} />Instructions</h4><p>{t}</p></div>
                            ))}
                            {unique(viewing.list.map((s) => s.learningsFocus)).map((t) => (
                                <div key={t} className="dlp-note"><h4><BookOpen size={16} />Learning Focus</h4><p>{t}</p></div>
                            ))}
                        </div>
                        <Attachments items={attachmentsOf(viewing.list)} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default DailySchedule;
